import { useEffect, useMemo, useState } from 'react';
import { fetchVideoMetadata } from '~/shared/components/Video/utils';
import { isVideo, Media } from '../media';

/**
 * Extracts information (e.g, alt, title, etc.) from product media to use for accessibility.
 */
export const useProductThumbnailLabels = (media: Media[]) => {
    const [videoTitles, setVideoTitles] = useState<Record<string, string>>({});

    const videoSources = useMemo(() => media.filter(isVideo), [media]);

    useEffect(() => {
        let isCurrent = true;

        Promise.all(
            videoSources.map((source) =>
                fetchVideoMetadata(source)
                    .then((metadata) => [source, metadata.title] as const)
                    // A missing title costs the thumbnail its label, not the gallery its render.
                    .catch(() => [source, ''] as const),
            ),
        ).then((entries) => {
            if (isCurrent) {
                setVideoTitles(Object.fromEntries(entries.filter(([, title]) => !!title)));
            }
        });

        return () => {
            isCurrent = false;
        };
    }, [videoSources]);

    return useMemo(
        () =>
            media.map((item, index) => {
                const position = `${index + 1} af ${media.length}`;

                if (isVideo(item)) return videoTitles[item] || `Vis video ${position}`;

                return item.alt || item.title || `Vis billede ${position}`;
            }),
        [media, videoTitles],
    );
};
